import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_TODAY } from '../../graphql';
import { useAddons } from './addons';
import { REACH_CALENDAR, ReachPost } from '../reach/reach';

/**
 * What Today shows, shared with the header's Today badge so the two always
 * agree (and Apollo sends each request once): tinycmsToday for the window,
 * Reach's calendar for the same window (its `waiting` list ignores the window),
 * and campaigns with posts in review.
 */

export type TodayKind = 'article' | 'page' | 'form' | 'site';

export interface TodayWaiting {
  kind: TodayKind;
  id: string;
  title: string | null;
  path: string | null;
  note: string | null;
  heroUrl: string | null;
  live: boolean;
  updatedAt: string | null;
  workingHash: string;
  checks: { blocking: number; warnings: number; first: string | null; more: number } | null;
}

export interface TodayScheduled {
  kind: TodayKind | 'dataset';
  id: string;
  title: string | null;
  path: string | null;
  at: string;
  cat: string | null;
}

export interface TodayApproval {
  id: string;
  kind: string;
  subjectId: string;
  action: 'publish' | 'schedule' | 'unpublish';
  approvedBy?: string;
  via?: 'ui' | 'mcp';
  subjectTitle?: string | null;
  subjectPath?: string | null;
  versionNumber?: number | null;
  scheduledFor?: string | null;
  recordedAt: string;
  stillLive: boolean;
}

export interface TodayAgent {
  at: string;
  group: 'article' | 'page' | 'site' | 'form' | 'media' | 'social';
  count: number;
}

export interface TodayData {
  from: string;
  to: string;
  waiting: TodayWaiting[];
  scheduled: TodayScheduled[];
  approvals: TodayApproval[];
  agent: TodayAgent[];
}

export interface CampaignPost {
  id: string;
  title?: string | null;
  status: string;
  scheduledDate?: string | null;
  targets: Array<{ id: string; platform: string }>;
}
export interface TodayCampaign {
  id: string;
  name: string;
  kind: 'campaign' | 'always_on';
  status: string;
  deadline?: string | null;
  landingPageUrl?: string | null;
  createdAt: string;
  posts: CampaignPost[];
}

const TODAY_CAMPAIGNS = gql`
  query TodayCampaigns($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      name
      kind
      status
      deadline
      landingPageUrl
      createdAt
      posts {
        id
        title
        status
        scheduledDate
        targets {
          id
          platform
        }
      }
    }
  }
`;

export type Span = 'day' | 'week';

/** Local midnight today → tomorrow (day), or the six days before today too (week). */
export function todayWindow(span: Span, now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (span === 'week') start.setDate(start.getDate() - 6);
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function useToday(span: Span = 'day', { skip = false } = {}) {
  const { accountId } = useAccount();
  const { reachOn } = useAddons();
  // Keyed by the calendar day so a page left open rolls over at midnight on refetch.
  const dayKey = new Date().toDateString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const win = useMemo(() => todayWindow(span), [span, dayKey]);

  const today = useQuery<{ tinycmsToday: TodayData }>(GET_TINYCMS_TODAY, {
    variables: { accountId, ...win },
    skip: skip || !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const reach = useQuery<{ reachCalendar: { posts: ReachPost[]; waiting: ReachPost[] } }>(
    REACH_CALENDAR,
    {
      variables: { accountId, ...win },
      skip: skip || !accountId || !reachOn,
      fetchPolicy: 'cache-and-network',
    }
  );
  const campaignsQ = useQuery<{ projects: TodayCampaign[] }>(TODAY_CAMPAIGNS, {
    variables: { accountId },
    skip: skip || !accountId || !reachOn,
    fetchPolicy: 'cache-and-network',
  });

  const data = today.data?.tinycmsToday || null;
  const waitingPosts = useMemo(() => reach.data?.reachCalendar.waiting || [], [reach.data]);
  const posts = useMemo(() => reach.data?.reachCalendar.posts || [], [reach.data]);
  // Dated campaigns with tactics in review, and always-on campaigns an agent set
  // up that the person hasn't turned on yet.
  const campaigns = useMemo(
    () =>
      (campaignsQ.data?.projects || []).filter((c) =>
        c.kind === 'always_on'
          ? c.status === 'planning'
          : c.posts.some((p) => p.status === 'in_review')
      ),
    [campaignsQ.data]
  );
  // Posts that belong to a campaign in review are approved with the campaign.
  const need = (data?.waiting.length || 0) + waitingPosts.length + campaigns.length;

  const refetch = () =>
    Promise.all([
      today.refetch(),
      reachOn ? reach.refetch() : null,
      reachOn ? campaignsQ.refetch() : null,
    ]);

  return {
    window: win,
    loading: today.loading && !data,
    error: today.error,
    data,
    waitingPosts,
    posts,
    campaigns,
    need,
    reachOn,
    refetch,
  };
}
